import {Component, OnDestroy, OnInit} from '@angular/core';
import {PageGlobalParameters} from '@models/pages/page-global-parameters';
import {MenuItem} from '@models/sidebar/menu-item';
import {ActivatedRoute} from '@angular/router';
import {PagesHelperService} from '@services/pages/pages-helper.service';
import {BreadcrumbService} from '@services/breadcrumb/breadcrumb.service';
import {takeUntil} from 'rxjs/operators';
import {
  StatusEventGroupPageDataService
} from '@services/pages/status-event-group-page/data/status-event-group-page-data.service';
import {ReplaySubject} from 'rxjs';
import {
  StatusEventGroupPageDisplayDetails, StatusEventGroupPageDisplayDetailsInputData, StatusEventPageData
} from '@models/pages/status-event-group-page/status-event-group-page-display-details';
import {SortEvent} from '@shared/table-header-sortable/table-header-sortable.directive';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {FilterEvent} from '@models/pages/status-event-group-page/header-filter-event';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {ITEMS_PER_PAGE_OPTIONS} from '@constants/pagination/pagination.constant';

@Component({
    selector: 'scitis-status-event-group-page-container',
    templateUrl: './status-event-group-page-container.component.html',
    styleUrls: ['./status-event-group-page-container.component.scss'],
    standalone: false
})
export class StatusEventGroupPageContainerComponent implements OnInit, OnDestroy {

  public pageGlobalParameters: PageGlobalParameters = new PageGlobalParameters();
  public routes: MenuItem[] = [];
  public statusEvents: StatusEventGroupPageDisplayDetails[] = [];
  public unfilteredStatusEvents: StatusEventGroupPageDisplayDetails[] = [];
  public errorCount: number = 0;
  public filtering: FilterEvent = new FilterEvent();
  public itemsPerPageOptions: number[] = ITEMS_PER_PAGE_OPTIONS;
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'statusEventGroupPageId',
  });

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private pagesHelperService: PagesHelperService,
    private breadcrumbService: BreadcrumbService,
    private statusEventPageGroupService: StatusEventGroupPageDataService,
    private tableHeaderSortableService: TableHeaderSortableService,
    private loadingIndicator: LoadingIndicatorService
  ) {
  }

  ngOnInit(): void {
    this.pageGlobalParameters = this.pagesHelperService.getPageDetailsFromUrl(this.activatedRoute.snapshot.paramMap);
    this.generateRoutes(this.pageGlobalParameters.associatedMenuItem);
    this.getStatusEventGroupPageData();
  }

  public onItemsPerPageChange(itemPerPage: number): void {
    this.paginateSettings = {...this.paginateSettings, itemsPerPage: itemPerPage, currentPage: 1};
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings.currentPage = pageNumber;
  }

  public getStatusEventGroupPageData(): void {
    this.loadingIndicator.show();
    this.unfilteredStatusEvents = [];
    this.errorCount = 0;
    this.statusEventPageGroupService.getStatusEventGroupPageData(+this.pageGlobalParameters.machineryGroupId, +this.pageGlobalParameters.pageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: StatusEventGroupPageDisplayDetailsInputData) => {
        response.machineStatusEventDetails.forEach(({machineName, machineId, machineLocation, machineStatusEventPageDetails}) => {
          const statusEventPageData: StatusEventPageData[] = [];
          machineStatusEventPageDetails.forEach(({statusEventsDetails, statusEventPageId}) => {
            for (const key in statusEventsDetails) {
              const signalName = statusEventsDetails[key].name;
              const signalData: StatusEventPageData = {
                events: [],
                statusEventPageId,
                signalName
              };
              if (!statusEventPageData.find((item) => signalName === item.signalName)) {
                signalData.events = response.statusEventPageData[signalName] || [];
                statusEventPageData.push(signalData);
              }
            }
          });
          const item = new StatusEventGroupPageDisplayDetails(machineName, machineId, machineLocation, statusEventPageData);
          this.errorCount += item.totalCount;
          if (item.statusEventPageData.length) {
            this.unfilteredStatusEvents.push(item);
          }
          this.statusEvents = this.unfilteredStatusEvents;
        });
        this.loadingIndicator.hide();
      }, error => {
        this.loadingIndicator.hide();
        console.log(error);
      });
  }

  private generateRoutes(menuItem: MenuItem): void {
    this.routes = this.breadcrumbService.generateRoutes(menuItem);
  }

  public onEventSort({column, direction}: SortEvent): void {
    this.statusEvents = this.tableHeaderSortableService.sortItems(this.unfilteredStatusEvents, column, direction);
  }

  public onFilter($event: FilterEvent): void {
    this.filtering = $event;
    this.statusEvents = this.statusEventPageGroupService.filterEvents(this.unfilteredStatusEvents, $event);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
